import { AuxTabBar } from "@blk/aladdin-react-components-es";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { InsGridWidget } from "components";
import { PrivateBenchmarkListColDefs, getPrivateBenchmarkRows } from "components/config/grid-templates/private-benchmarks-list";
import { language } from "language/default";
import { removeAllSelectedRow, removeSelectedRow, setSelectedRowsState } from "store/grids";
const BenchmarkTabs = {
    Blended: "Blended",
    RuleBased: "Rule-based",
    Standard: "Standard",
    Uploaded: "Uploaded"
};
const CompactColumnWidths = {
    geography: { maxWidth: 130, minWidth: 110, width: 120 },
    source: { maxWidth: 130, minWidth: 110, width: 120 },
    strategy: { maxWidth: 120, minWidth: 100, width: 110 },
    vintage: { maxWidth: 100, minWidth: 80, width: 90 }
};
const BenchmarkColumnFields = {
    geography: "geography",
    source: "source",
    strategy: "strategy",
    vintage: "vintage"
};
export const BenchmarkTypes = {
    Blended: "BlendedBenchmark",
    RuleBased: "UserDefinedBenchmark",
    Standard: "StandardBenchmark",
    Uploaded: "CustomBenchmark"
};
const getRowsByTab = (benchmarks, selectedTab) => {
    const typeMap = {
        [BenchmarkTabs.Blended]: BenchmarkTypes.Blended,
        [BenchmarkTabs.Uploaded]: BenchmarkTypes.Uploaded,
        [BenchmarkTabs.Standard]: BenchmarkTypes.Standard,
        [BenchmarkTabs.RuleBased]: BenchmarkTypes.RuleBased
    };
    const type = typeMap[selectedTab] || BenchmarkTypes.Standard;
    return benchmarks.filter((r) => r?.summary?.type === type);
};
const getColumnDefs = (compactColumns, showReferenceDateColumns) => {
    let cols = PrivateBenchmarkListColDefs;
    if (showReferenceDateColumns) {
        cols = cols.map((colDef) => colDef.field === "constituents" || colDef.field === "medianNetIrr" || colDef.field === "medianTvpi"
            ? { ...colDef, hide: false }
            : colDef);
    }
    if (!compactColumns) {
        return cols;
    }
    const compactFieldMap = {
        [BenchmarkColumnFields.geography]: CompactColumnWidths.geography,
        [BenchmarkColumnFields.source]: CompactColumnWidths.source,
        [BenchmarkColumnFields.strategy]: CompactColumnWidths.strategy,
        [BenchmarkColumnFields.vintage]: CompactColumnWidths.vintage
    };
    return cols.map((colDef) => colDef.field && compactFieldMap[colDef.field]
        ? { ...colDef, ...compactFieldMap[colDef.field] }
        : colDef);
};
const InsBenchmarkSelectionGridComponent = ({ activeBenchmarkKey, benchmarks, compactColumns, gridId, onRowSelected, queryState, refreshAction, showReferenceDateColumns }) => {
    const dispatch = useDispatch();
    const [selectedTab, setSelectedTab] = useState(BenchmarkTabs.Standard);
    const rowSelectionHandler = useCallback((event) => {
        if (!event.data) {
            return;
        }
        if (event.node.isSelected()) {
            dispatch(setSelectedRowsState({
                id: gridId,
                selectedRows: [event.data.id]
            }));
            onRowSelected?.(event.data);
        }
        else {
            dispatch(removeSelectedRow({
                id: gridId,
                row: event.data.id
            }));
            const selectedRows = event.api.getSelectedRows();
            if (selectedRows.length === 0) {
                onRowSelected?.(null);
            }
        }
    }, [dispatch, gridId, onRowSelected]);
    const rowData = useMemo(() => getPrivateBenchmarkRows(getRowsByTab(benchmarks, selectedTab), () => true), [benchmarks, selectedTab]);
    const columnDefs = useMemo(() => getColumnDefs(compactColumns, showReferenceDateColumns), [compactColumns, showReferenceDateColumns]);
    return React.createElement(React.Fragment, null,
        React.createElement("div", { style: { marginBottom: "-1px" } },
            React.createElement(AuxTabBar, { onTabBarItemSelected: (event) => {
                    setSelectedTab(event.detail.tab.uid);
                    dispatch(removeAllSelectedRow(gridId));
                }, tabType: "classic", activeUid: selectedTab, data: [
                    { label: language.en.privateBenchmarks.Standard, uid: BenchmarkTabs.Standard },
                    { label: language.en.privateBenchmarks.Uploaded, uid: BenchmarkTabs.Uploaded },
                    { label: language.en.privateBenchmarks.RuleBased, uid: BenchmarkTabs.RuleBased },
                    { label: language.en.privateBenchmarks.Blended, uid: BenchmarkTabs.Blended }
                ] })),
        React.createElement(InsGridWidget, { stateId: gridId, rowData: rowData, selectionMode: "single", refreshAction: refreshAction, selectedKeys: activeBenchmarkKey ? [activeBenchmarkKey] : undefined, keyField: "id", queryState: queryState, columnDefs: columnDefs, rowSelectedHandler: rowSelectionHandler, suppressRowClickSelection: false }));
};
export const InsBenchmarkSelectionGrid = memo(InsBenchmarkSelectionGridComponent);
